import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Document, Page } from 'react-pdf';
import printJS from 'print-js';
import Swiper from 'swiper';
import { Pagination, Navigation } from 'swiper/modules';
import { NavigationOptions } from 'swiper/types';
import { SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';

import { Backdrop, CircularProgress, Dialog, DialogContent, Link, List, ListItem, TextField } from '@mui/material';
import {
  CoreBox,
  CoreCheckbox,
  CoreFormControl,
  CoreFormControlLabel,
  CoreFormGroup,
  CoreTypography,
} from '@youscience/core';

import DialogFooter, { DialogFooterProps } from '@components/DialogFooter';
import ActionButton from '@components/ActionButton';
import MonthDayYearDatePicker from '@components/MonthDayYearDatePicker';
import useRecommenderWorkspaceStore from '@store/RecommenderWorkspaceStore';
import { usePdfPrint } from '@hooks/usePdfPrint';

import { ROUTES } from '@constants/ROUTES';

import PrintIcon from '@images/print-icon.svg?react';
import DownloadIcon from '@images/download-icon.svg?react';

import { sxStyles, StyledDialogTitle, StyledPdf, StyledSwiper } from './ReviewPdf.styles';

export enum ReviewTypes {
  PREVIEW_UPLOAD = 'preview_upload',
  PREVIEW_FORM = 'preview_form',
  REVIEW_FORM = 'review_form',
  REVIEW_AND_SUBMIT = 'review_and_submit',
}

const getHeaderTitle = (reviewType: ReviewTypes) => {
  const titles: { [key in ReviewTypes]: string } = {
    [ReviewTypes.PREVIEW_UPLOAD]: 'Review the PDF preview of your file',
    [ReviewTypes.PREVIEW_FORM]: 'Preview form',
    [ReviewTypes.REVIEW_FORM]: 'Review form',
    [ReviewTypes.REVIEW_AND_SUBMIT]: 'Review and submit',
  };

  return titles[reviewType] || '';
};

interface ReviewPdfErrorsProps {
  form?: boolean;
  recommenderProfile?: boolean;
  schoolProfile?: boolean;
}

interface ReviewPdfProps {
  title?: string;
  subtitle?: string;
  isOpen: boolean;
  isLoading: boolean;
  reviewType: ReviewTypes;
  pdf?: string;
  downloadedFileName?: string;
  onClose: () => void;
  onContinue: (signature?: string, date?: string, memberIds?: number[]) => void;
  errors?: ReviewPdfErrorsProps;
  members?: { name: string; id: number }[];
}

export const ReviewPdf = (props: ReviewPdfProps) => {
  const {
    title,
    subtitle,
    isOpen,
    isLoading,
    pdf,
    reviewType,
    downloadedFileName,
    onClose,
    onContinue,
    errors,
    members = [],
  } = props;
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(6);
  const [agreementStatus, setAgreementStatus] = useState<{ [key: string]: boolean }>({});
  const [isGoNextEnabled, setIsGoNextEnabled] = useState(false);
  const [signature, setSignature] = useState('');
  const [date, setDate] = useState('');
  const [dateHelperText, setDateHelperText] = useState('');
  const [memberIds, setMemberIds] = useState<number[]>([]);
  const { printPdf } = usePdfPrint();
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }): void => {
    setNumPages(numPages);
  };
  const navigate = useNavigate();
  const { isError } = useRecommenderWorkspaceStore();

  const pdfFile = useMemo(() => {
    if (!pdf) return null;

    try {
      // For react-pdf v9, we need to use a data URI with the base64 prefix
      const dataUri = pdf.startsWith('data:application/pdf;base64,') ? pdf : `data:application/pdf;base64,${pdf}`;

      return dataUri;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error processing PDF:', error);
      return null;
    }
  }, [pdf]);

  useEffect(() => {
    setPageNumber(1);
    setNumPages(0);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && isError) onClose();
  }, [isError]);

  useEffect(() => {
    if (reviewType === ReviewTypes.PREVIEW_FORM || reviewType === ReviewTypes.PREVIEW_UPLOAD) {
      setIsGoNextEnabled(agreementStatus['affirmation']);
    } else if (reviewType === ReviewTypes.REVIEW_AND_SUBMIT) {
      const isSelectedFinalReportSchools = members?.length > 0 ? memberIds.length > 0 : true;

      setIsGoNextEnabled(
        agreementStatus['affirmation1'] &&
          agreementStatus['affirmation2'] &&
          signature !== '' &&
          date !== '' &&
          isSelectedFinalReportSchools,
      );
    }
  }, [agreementStatus, date, signature, memberIds]);

  const navigationPrevRef = useRef<HTMLDivElement>(null);
  const navigationNextRef = useRef<HTMLDivElement>(null);

  const handleBeforeInit = (swiper: Swiper) => {
    // eslint-disable-next-line no-param-reassign
    swiper.params.navigation = {
      ...(swiper.params.navigation as NavigationOptions),
      prevEl: navigationPrevRef.current,
      nextEl: navigationNextRef.current,
    };
  };

  const handleDateChange = (date: Date | null, error?: { validationError: string | null }) => {
    if (date && !error?.validationError) {
      setDate(
        `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date
          .getDate()
          .toString()
          .padStart(2, '0')}/${date.getFullYear()}`,
      );
    } else {
      setDate('');
    }
  };

  const handleDateError = (fieldName: string, error: string | undefined) => {
    let errorText = '';

    if (error === 'minDate' || error === 'maxDate') {
      errorText = 'Please enter a date within the range of yesterday and tomorrow.';
    } else if (error === 'invalidDate') {
      errorText = 'Please enter a valid date';
    }

    setDateHelperText(errorText);
  };

  const handleDownload = () => {
    if (!pdf) return;

    try {
      // Method 1: Try the binary conversion approach first
      try {
        // Create a binary blob from the base64 data
        const binaryString = atob(pdf);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        // Create a blob and URL for download
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = downloadedFileName || title || 'preview.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        // Method 2: Fall back to data URI approach if binary conversion fails
        // eslint-disable-next-line no-console
        console.log('Falling back to data URI download method');
        const dataUri = pdf.startsWith('data:application/pdf;base64,') ? pdf : `data:application/pdf;base64,${pdf}`;

        const link = document.createElement('a');
        link.href = dataUri;
        link.download = downloadedFileName || title || 'preview.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error downloading PDF:', error);
    }
  };

  const handlePrint = () => {
    if (!pdf) return;
    printPdf(pdf, downloadedFileName || title || 'preview.pdf');
  };

  const handlePreviewFormAgreement = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAgreementStatus((prevState) => {
      return { ...prevState, [e.target.name]: e.target.checked };
    });
  };

  const handleLinkClick = (to: 'form' | 'recommender' | 'school') => {
    const url = new URL(window.location.href);
    const showingErrors = new URLSearchParams(window.location.search).get('showErrors') === 'true';
    switch (to) {
      case 'form':
        onClose();
        if (!showingErrors) url.searchParams.append('showErrors', 'true');
        window.history.replaceState({}, '', url.toString());
        break;
      case 'recommender':
        onClose();
        navigate({ pathname: ROUTES.RECOMMENDER_PROFILE, search: 'showErrors=true' });
        break;
      case 'school':
        onClose();
        navigate({ pathname: ROUTES.SCHOOL_PROFILE, search: 'showErrors=true' });
        break;
      default:
        break;
    }
  };

  const getDialogContent = () => {
    if (Object.values(errors || {}).some(Boolean)) {
      return (
        <CoreBox sx={sxStyles.dialogContent}>
          <CoreBox sx={sxStyles.dialogContentTitle}>
            <CoreTypography variant="h5">{title}</CoreTypography>

            <CoreTypography>Please review and submit:</CoreTypography>
          </CoreBox>

          <List sx={{ listStyleType: 'disc' }}>
            {errors?.recommenderProfile && (
              <ListItem sx={{ display: 'list-item' }}>
                <Link onClick={() => handleLinkClick('recommender')} sx={sxStyles.link}>
                  Your profile
                </Link>
              </ListItem>
            )}

            {errors?.schoolProfile && (
              <ListItem sx={{ display: 'list-item' }}>
                <Link onClick={() => handleLinkClick('school')} sx={sxStyles.link}>
                  School profile
                </Link>
              </ListItem>
            )}

            {errors?.form && (
              <ListItem sx={{ display: 'list-item' }}>
                All sections of{' '}
                <Link sx={sxStyles.link} onClick={() => handleLinkClick('form')}>
                  {title}
                </Link>
              </ListItem>
            )}
          </List>
        </CoreBox>
      );
    } else if (pdf) {
      return (
        <CoreBox sx={sxStyles.dialogContent}>
          <CoreBox sx={sxStyles.dialogContentTitle}>
            {title && <CoreTypography variant="h5">{title}</CoreTypography>}

            {subtitle && <CoreTypography>{subtitle}</CoreTypography>}
          </CoreBox>

          <CoreBox sx={sxStyles.dialogContentPdf}>
            <StyledPdf isLoading={numPages === 0} sx={{ height: '100%', width: '100%' }}>
              <Document
                loading={<CircularProgress />}
                file={pdfFile}
                onLoadSuccess={onDocumentLoadSuccess}
                error={
                  <CoreBox sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 3 }}>
                    <CoreTypography color="error" gutterBottom>
                      Failed to load PDF directly. Please try downloading the file instead.
                    </CoreTypography>
                    <ActionButton
                      onClick={handleDownload}
                      startIcon={<DownloadIcon />}
                      variant="contained"
                      sx={{ mt: 2 }}
                    >
                      Download PDF
                    </ActionButton>
                  </CoreBox>
                }
                noData={<CoreTypography>No PDF data available.</CoreTypography>}
              >
                <Page pageNumber={pageNumber} renderAnnotationLayer={false} renderTextLayer={false} />
              </Document>
            </StyledPdf>

            <StyledSwiper
              slidesPerView={1}
              slidesPerGroup={1}
              pagination={{ clickable: true }}
              navigation={{
                nextEl: navigationNextRef.current,
                prevEl: navigationPrevRef.current,
              }}
              modules={[Pagination, Navigation]}
              onBeforeInit={handleBeforeInit}
              onSlideChange={(swiperCore) => {
                setPageNumber(swiperCore.activeIndex + 1);
              }}
            >
              {numPages
                ? Array.from({ length: numPages }, (_, i) => i).map((page) => (
                    <SwiperSlide key={page}>
                      <CoreBox sx={{ width: '0' }}></CoreBox>
                    </SwiperSlide>
                  ))
                : null}
            </StyledSwiper>
          </CoreBox>

          <CoreBox sx={sxStyles.dialogContentAgreement}>{getAgreement(reviewType)}</CoreBox>

          <CoreBox sx={sxStyles.dialogContentSignature}>{getSignature(reviewType)}</CoreBox>

          <CoreBox sx={sxStyles.dialogContentAgreement}>{getMembers()}</CoreBox>
        </CoreBox>
      );
    } else {
      return <CoreBox sx={sxStyles.dialogContentPdf}></CoreBox>;
    }
  };

  const getAgreement = (reviewType: ReviewTypes) => {
    switch (reviewType) {
      case ReviewTypes.PREVIEW_UPLOAD:
        return (
          <>
            <CoreTypography variant="body2">
              Please confirm that your file looks exactly as you want it presented to colleges.
            </CoreTypography>

            <CoreFormControlLabel
              name="affirmation"
              label={<CoreTypography>Yes, this file looks exactly as I intended</CoreTypography>}
              control={<CoreCheckbox value={true} onChange={handlePreviewFormAgreement} defaultChecked={false} />}
            />
          </>
        );
      case ReviewTypes.PREVIEW_FORM:
      case ReviewTypes.REVIEW_FORM:
        return null;
      case ReviewTypes.REVIEW_AND_SUBMIT:
        return (
          <>
            <CoreTypography variant="body2">Please affirm the following before you submit your form.</CoreTypography>

            <CoreFormControlLabel
              name="affirmation1"
              label={
                <CoreTypography variant="body2">
                  I have reviewed the PDF copy of the form and I wish to proceed with submission.
                </CoreTypography>
              }
              control={<CoreCheckbox value={true} onChange={handlePreviewFormAgreement} defaultChecked={false} />}
            />

            <CoreFormControlLabel
              name="affirmation2"
              label={
                <>
                  <CoreTypography variant="body2"> By signing and submitting this form,</CoreTypography>

                  <List dense>
                    <ListItem dense>
                      <CoreTypography variant="body2">
                        I affirm that the form and all accompanying documents are not customized for a particular
                        institution.
                      </CoreTypography>
                    </ListItem>

                    <ListItem dense>
                      <CoreTypography variant="body2">
                        I understand that after I submit, I cannot edit or revoke my submission.
                      </CoreTypography>
                    </ListItem>

                    <ListItem dense>
                      <CoreTypography variant="body2">
                        I understand that these forms are available to every college that this student applied to using
                        Common App.
                      </CoreTypography>
                    </ListItem>
                  </List>
                </>
              }
              control={<CoreCheckbox value={true} onChange={handlePreviewFormAgreement} defaultChecked={false} />}
            />
          </>
        );
      default:
        return null;
    }
  };

  const getSignature = (reviewType: ReviewTypes) => {
    const minDate = new Date();
    const maxDate = new Date();
    minDate.setDate(minDate.getDate() - 1);
    maxDate.setDate(maxDate.getDate() + 1);

    switch (reviewType) {
      case ReviewTypes.PREVIEW_UPLOAD:
        return null;
      case ReviewTypes.PREVIEW_FORM:
      case ReviewTypes.REVIEW_FORM:
        return null;
      case ReviewTypes.REVIEW_AND_SUBMIT:
        return (
          <CoreBox sx={sxStyles.dialogContentSignature}>
            <CoreFormControl fullWidth>
              <TextField
                label="Signature"
                value={signature}
                required
                onChange={(e) => setSignature(e.target.value)}
                autoComplete="off"
              />
            </CoreFormControl>

            <CoreFormControl fullWidth>
              <MonthDayYearDatePicker
                name="date"
                label="Date"
                required
                slotProps={{
                  inputProps: { clearable: true, onClear: () => handleDateChange },
                  error: !!dateHelperText,
                }}
                value={date}
                handleDateChange={handleDateChange}
                handleDateError={handleDateError}
                error={dateHelperText}
                maxDate={maxDate}
                minDate={minDate}
              />
            </CoreFormControl>
          </CoreBox>
        );
      default:
        return null;
    }
  };

  const handleParentCheckBoxChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setMemberIds(members.map((member) => member.id));
    } else {
      setMemberIds([]);
    }
  };

  const handleCheckBoxChange = (event: ChangeEvent<HTMLInputElement>) => {
    let updatedMemberIds = [...memberIds];

    if (updatedMemberIds.includes(+event.target.value)) {
      updatedMemberIds = updatedMemberIds.filter((memberId) => memberId !== +event.target.value);
    } else {
      updatedMemberIds.push(+event.target.value);
    }

    setMemberIds(updatedMemberIds);
  };

  const getMembers = () => {
    if (!members || members.length === 0) return;

    const isAllSchoolsSelected = members.length === memberIds.length;

    switch (reviewType) {
      case ReviewTypes.PREVIEW_UPLOAD:
        return null;
      case ReviewTypes.PREVIEW_FORM:
      case ReviewTypes.REVIEW_FORM:
        return null;
      case ReviewTypes.REVIEW_AND_SUBMIT:
        return (
          <>
            <CoreTypography variant="subtitle2" component="h4">
              Please submit this report only to the college where your student intends to enroll. This college should
              have either accepted or waitlisted your student. You may select more than one college only in the event of
              a wait list situation.
            </CoreTypography>

            <CoreTypography variant="body2">Select colleges.</CoreTypography>

            <CoreFormGroup>
              <CoreFormControlLabel
                label={<CoreTypography variant="body2">Select all</CoreTypography>}
                control={
                  <CoreCheckbox
                    checked={isAllSchoolsSelected}
                    indeterminate={memberIds.length > 0 && !isAllSchoolsSelected}
                    onChange={handleParentCheckBoxChange}
                  />
                }
              />

              {members.map((member) => (
                <CoreFormControlLabel
                  key={member.id}
                  label={<CoreTypography variant="body2">{member.name}</CoreTypography>}
                  control={
                    <CoreCheckbox
                      value={member.id}
                      checked={memberIds.includes(member.id)}
                      onChange={handleCheckBoxChange}
                    />
                  }
                />
              ))}
            </CoreFormGroup>
          </>
        );
      default:
        return null;
    }
  };

  const footerProps = (): DialogFooterProps => {
    switch (reviewType) {
      case ReviewTypes.PREVIEW_UPLOAD:
        return {
          goBackButtonTitle: 'Cancel',
          goNextButtonTitle: 'Save',
          isDisableNextButton: !isGoNextEnabled,
          onGoBack: onClose,
          onGoNext: () => {
            onClose();
            onContinue();
          },
        };
      case ReviewTypes.PREVIEW_FORM:
      case ReviewTypes.REVIEW_FORM:
        return {
          goBackButtonTitle: 'Go back to form',
          onGoBack: onClose,
        };
      case ReviewTypes.REVIEW_AND_SUBMIT:
        return {
          goBackButtonTitle: `Back to ${title?.toLowerCase()}`,
          goNextButtonTitle: 'Submit',
          isDisableNextButton: !isGoNextEnabled,
          onGoBack: onClose,
          onGoNext: () => {
            onClose();
            onContinue(signature, date, memberIds);
          },
        };
      default:
        return {};
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullScreen={true}
      maxWidth={false}
      PaperProps={{
        style: { height: '100vh', bottom: '0', position: 'fixed' },
      }}
      hideBackdrop={true}
    >
      <StyledDialogTitle>
        <CoreBox sx={sxStyles.dialogHeader}>
          <CoreTypography variant="h6" sx={sxStyles.dialogTitle}>
            {getHeaderTitle(reviewType)}
          </CoreTypography>

          <CoreBox sx={sxStyles.dialogTitleButtons}>
            <ActionButton
              disabled={numPages === 0}
              sx={sxStyles.dialogTitleButton}
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              variant="text"
            >
              Download
            </ActionButton>

            <ActionButton
              disabled={numPages === 0}
              sx={sxStyles.dialogTitleButton}
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              title="Print"
              variant="text"
            >
              Print
            </ActionButton>
          </CoreBox>
        </CoreBox>
      </StyledDialogTitle>

      <DialogContent sx={{ padding: '0' }}>
        <CoreBox sx={{ ...sxStyles.dialogMain }}>
          <Backdrop
            open={isLoading ?? false}
            invisible
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, position: 'absolute' }}
          >
            <CircularProgress />
          </Backdrop>

          {getDialogContent()}
        </CoreBox>
      </DialogContent>

      <DialogFooter {...footerProps()} />
    </Dialog>
  );
};
